// Phase 40 Batch BD — Notifications preferences card.
//
// Settings → Notifications section. Lets the authenticated user toggle
// the "Referral milestone emails" opt-out flag. Currently the only
// notification class wired in; future batches (welcome digest, weekly
// portfolio summary, etc.) add their own rows under the same heading.
//
// On mount: reads the initial value from the AuthContext's `user`
// object (which `/api/auth/me` populates with
// `referral_notifications_opt_out`). On toggle: PATCH
// /api/me/notification-prefs and refresh the AuthContext from the
// response so the rest of the app sees the new value.

import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell } from "lucide-react";

export default function NotificationsCard() {
  const { user, refresh: refreshUser } = useAuth();
  // Render directly from `user.referral_notifications_opt_out` so the
  // toggle's displayed state is always the source-of-truth that
  // AuthContext is holding. Add an `override` state ONLY while a save
  // is in flight (optimistic UI). On success/failure the override is
  // cleared and we revert to `user`.
  const [override, setOverride] = useState(null);  // null | true | false
  const [saving, setSaving] = useState(false);

  const persistedOptOut = user?.referral_notifications_opt_out === true;
  const enabled = override === null ? !persistedOptOut : override;

  const handleToggle = async (next) => {
    setOverride(next);
    setSaving(true);
    try {
      await api.patch("/me/notification-prefs", {
        referral_notifications_opt_out: !next,
      });
      // Refresh the AuthContext so other surfaces see the new value.
      if (typeof refreshUser === "function") {
        try { await refreshUser(); } catch { /* non-fatal */ }
      }
      toast.success(
        next ? "Milestone emails turned on" : "Milestone emails turned off",
      );
      setOverride(null);  // hand control back to the source-of-truth
    } catch {
      toast.error("Could not save — try again in a moment.");
      setOverride(null);  // revert to the user object's value
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="notifications-section"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <header className="mb-6 flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <Bell className="h-4 w-4" />
        </span>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Notifications</h2>
          <p className="mt-1 text-sm text-slate-500">
            Choose which RunIdea.ai emails land in your inbox.
          </p>
        </div>
      </header>

      <div
        className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50/40 p-4"
        data-testid="referral-milestone-pref-row"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            Referral milestone emails
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Get notified when friends sign up via your referral link
            (milestones at 1, 2, 3, 5, 10, 25, 50, and 100). No more than
            8 emails total — and you can unsubscribe from any of them.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={saving}
          onCheckedChange={handleToggle}
          aria-label="Referral milestone emails"
          data-testid="referral-milestone-pref-toggle"
        />
      </div>
    </section>
  );
}
